/**
 * --------------------------------------------------------------------------
 * Free Shipping Progress Component
 * --------------------------------------------------------------------------
 * Updates free shipping progress bars and messages based on cart state.
 * Supports both value-based (e.g., £10) and item-based thresholds.
 * 
 * This component integrates with the optimistic-cart.js system and updates
 * all elements with data-free-shipping-progress attribute on cart changes.
 */

(function() {
  'use strict';

  let hasInitialized = false;

  /**
   * Update all free shipping progress elements
   */
  function updateFreeShippingProgress(cart) {
    const progressElements = document.querySelectorAll('[data-free-shipping-progress]');
    
    if (progressElements.length === 0) {
      return;
    }

    if (!cart || !Array.isArray(cart.items)) {
      // If no cart data, show empty state for all elements
      progressElements.forEach((element) => {
        updateElement(element, 0, 0, 'value', 0);
      });
      return;
    }

    progressElements.forEach((element) => {
      // Get threshold type and value
      const thresholdType = element.getAttribute('data-threshold-type') || 'value';
      const thresholdAttr = element.getAttribute('data-threshold');
      
      if (!thresholdAttr) {
        console.warn('Free shipping progress: No threshold specified');
        return;
      }

      const threshold = parseFloat(thresholdAttr) || 0;
      let currentValue = 0;
      let remaining = 0;
      let progress = 0;

      if (thresholdType === 'value') {
        // Value-based threshold (in cents)
        currentValue = cart.total_price || 0; // Already in cents
        remaining = Math.max(0, threshold - currentValue);
        progress = threshold > 0 ? Math.min(100, (currentValue / threshold) * 100) : 0;
      } else {
        // Item-based threshold
        currentValue = cart.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        remaining = Math.max(0, threshold - currentValue);
        progress = threshold > 0 ? Math.min(100, (currentValue / threshold) * 100) : 0;
      }

      // Update this element
      updateElement(element, progress, remaining, thresholdType, threshold);
    });
  }

  /**
   * Update a single free shipping progress element
   */
  function updateElement(element, progress, remaining, thresholdType, threshold) {
    // Update progress bar (two-bar layout: green progress + gray remaining)
    const progressBar = element.querySelector('[data-free-shipping-progress-bar]');
    
    if (progressBar) {
      // Calculate progress percentage and set as percentage width
      // This matches the initial width set in Liquid and works better with responsive layouts
        const progressPercentage = Math.min(100, Math.max(0, progress));
      progressBar.style.width = `${progressPercentage}%`;
    }

    // Update remaining amount text
    const remainingElements = element.querySelectorAll('[data-free-shipping-remaining]');
    remainingElements.forEach((el) => {
      if (thresholdType === 'value') {
        // Format as currency using centralized utility
        // Convert cents to base amount
        const baseAmount = remaining / 100;
        
        // Get currency info
        const currency = window.Shopify?.currency || { active: 'GBP', rate: '1.0' };
        const currencyCode = currency.active || 'GBP';
        
        // Check if automatic currency conversion is enabled
        const convertCurrency = window.Shopify?.theme?.settings?.free_shipping_convert_currency !== false;
        
        // Apply rate conversion only if enabled
        let amountToFormat = baseAmount;
        if (convertCurrency) {
          const rate = parseFloat(currency.rate || '1.0');
          amountToFormat = baseAmount * rate;
        }
        
        // Use centralized utility if available
        if (window.theme?.utils?.money) {
          el.textContent = window.theme.utils.money(amountToFormat);
        } else {
          // Fallback formatting
          el.textContent = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          }).format(amountToFormat);
        }
      } else {
        // Format as item count
        const remainingItems = Math.ceil(remaining);
        el.textContent = remainingItems === 1 ? '1 item' : `${remainingItems} items`;
      }
    });
  }

  /**
   * Initialize the component
   */
  function init() {
    if (hasInitialized) {
      return;
    }

    hasInitialized = true;

    // Listen for cart updates
    window.addEventListener('cart:updated', function(event) {
      const cart = window.Shopify && window.Shopify.cart && window.Shopify.cart._cart;
      if (cart) {
        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(function() {
          updateFreeShippingProgress(cart);
        });
      }
    });

    // Listen for window resize to recalculate progress bar widths
    let resizeTimeout;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function() {
        const cart = window.Shopify && window.Shopify.cart && window.Shopify.cart._cart;
        if (cart) {
          updateFreeShippingProgress(cart);
        }
      }, 100);
    });

    // Initial update when DOM is ready
    function doInitialUpdate() {
      const cart = window.Shopify && window.Shopify.cart && window.Shopify.cart._cart;
      if (cart && Array.isArray(cart.items)) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(function() {
          updateFreeShippingProgress(cart);
        });
      } else {
        // Wait a bit and try again
        setTimeout(doInitialUpdate, 100);
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', doInitialUpdate);
    } else {
      doInitialUpdate();
    }
  }

  // Initialize on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

